import { MVF, mvfSchema } from "@lib/tranche";

export function validatePoints(
  mvf: number[][] | MVF,
  isHold: boolean,
): string | null {
  const points = getPoints(mvf);

  if (
    !Array.isArray(points) ||
    !points.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        typeof point[0] === "number" &&
        typeof point[1] === "number" &&
        !isNaN(point[0]) &&
        !isNaN(point[1]),
    )
  ) {
    return "Invalid text format";
  }

  if (points.length === 1 && points[0][0] === 0 && points[0][1] === 0) {
    return null;
  }

  if (points.length < 2) {
    return "There must be at least 2 points";
  }

  if (points[0][0] !== 0) {
    return `X coordinate of first point must be 0 (the first point is the y-intercept)`;
  }

  if (points[points.length - 1][1] !== 0) {
    return "Y coordinate of last point must by 0 (the last point is the x-intercept)";
  }

  if (isHold) {
    // There are two valid shapes for the Hold MVF:
    //   Two points at (0,0) and (x,0)
    //   Three points at (0,y), (x,y), and (x,0)
    // (The two-points case exists because the three-points case turns into it if y=0.)
    if (points.length === 2) {
      if (points[0][1] !== 0 || points[1][1] !== 0) {
        return "Hold must either have 3 points or have value 0";
      }
      if (points[0][0] !== 0) {
        return "The first point must have x=0";
      }
      if (points[1][0] <= 0) {
        return "The second point must have x>0";
      }
    } else if (points.length === 3) {
      if (points[0][1] !== points[1][1]) {
        return "The y values of the first 2 points of the hold function must be equal (the hold function must be horizontal at the start)";
      }

      if (points[1][0] !== points[2][0]) {
        return "The x values of the last 2 points of the hold function must be equal (the hold function must be vertical at the end)";
      }

      if (points[1][1] <= 0) {
        // TODO: TrancheEditor blows up if hold is set to 0
        return "The first 2 points must have a y value greater than 0.001";
      }
    } else {
      return "Hold must have exactly 3 points or be at value 0";
    }

    return null;
  }

  // Special-case for the all-zero piecewise function
  if (points.every((pt) => pt[0] === 0 && pt[1] === 0)) {
    return null;
  }

  for (let i = 1; i < points.length; i++) {
    // x must be increasing (equality is allowed)
    const xConsistent = points[i][0] >= points[i - 1][0];
    // y must be decreasing (equality is allowed)
    const yConsistent = points[i][1] <= points[i - 1][1];

    const consecutive0Height =
      i >= points.length - 1
        ? false
        : points[i - 1][1] === points[i][1] &&
          points[i][1] === points[i + 1][1];

    if (consecutive0Height) {
      return "consecutive 0 height points are not allowed";
    }

    if (!xConsistent) {
      return `Dollars are not monotonically increasing (${points[i][0]} must be >= ${points[i - 1][0]})`;
    }

    if (!yConsistent) {
      return `Values are not non-monotonically decreasing (${points[i][1]} must be ${isHold ? "<=" : "<"} ${points[i - 1][1]})`;
    }
  }

  return null;
}

function getPoints(mvf: number[][] | MVF): number[][] {
  if (mvfSchema.safeParse(mvf).success) {
    return mvf.map((point) => [point.dollar, point.value]);
  }

  return mvf as number[][];
}
